import Phaser from 'phaser';
import { STARTING_LIVES, CRUMBLE, nextCleanStreak } from '@/game/config';
import { timeoutMultFor, clockTrimFor, starProgress } from '@/game/level';
import { MatchClass, MatchTagForBannerContext, MatchNextInSentence, type BoardRule } from '@/game/rules/BoardRule';
import { getBestRoundMs, setBestRoundMs } from '@/game/storage';
import { crumbleThresholdMs, pace, spreadRatio, pushBoardTime } from '@/game/crumble';
import { getActivePack, packKey } from '@/game/packs';
import { isJourneyPool } from '@/data/pools';
import { devFlag, devParam } from '@/game/dev';
import { screenWakeLock } from '@/platform/wakeLock';
import {
  isClipMode, isClipLessonSpent, recordClipBoard, clipBoardsPlayed, boardsPerLesson,
} from '@/game/clip';
import { ReadingProgression, type PoolStats } from '@/game/progression/ReadingProgression';
import {
  UFO_AT_STACK_STARS, DIAMOND_AT_STACK_STARS, ICE_AT_STACK_STARS, GALAXY_AT_STACK_STARS,
  SAT_AT_STACK_STARS, PASTA_AT_STACK_STARS, CAT_AT_STACK_STARS, WORM_AT_STACK_STARS,
  ROCKET_AT_STACK_STARS, isRocketReadLang,
} from '@/game/progression/config';
import { wordInClass } from '@/data/packs/membership';
import { milestoneAt, milestoneReached } from '@/game/milestones';
import {
  trackRoundStart, trackBoardCleared, trackRoundEnd, trackPlanetReached,
} from '@/analytics';
import { recordRoundPlayed } from '@/game/qrPromo';
import { boardIconAssets } from '@/game/wordIcons';
import type { BoardInit } from './BoardScene';
import type { Pack, PackSet, PackWord } from '@/data/packs/types';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';
import { registerSmash } from '@/game/progression/streakSession';

export interface BoardCompletion {
  elapsedMs: number;
  isRecord: boolean;
}

/**
 * How long advanceBoard waits for the next board's icons before restarting the Board scene without
 * them (see warmBoardAssets). Long enough to cover a cache read and a slow-ish fetch, short enough
 * that a finished board never looks frozen: past this the board comes up and BoardScene's own
 * preload finishes the job.
 */
const WARM_TIMEOUT_MS = 400;

export class RoundScene extends Phaser.Scene {
  lives = STARTING_LIVES;
  score = 0;
  boardNumber = 1;
  bestRoundMs: number | null = null;
  /**
   * Boards cleared back-to-back without losing a heart. Every PERFECT_STREAK_STEP of them earns the
   * ABC-star "Perfekt!" overlay (BoardScene.showNextRoundBanner) — the one reward for accuracy
   * rather than volume, since score cannot express it: a wrong tap costs 0 points, only a heart.
   *
   * "Without error" deliberately means "without losing a heart", which includes a target that fell
   * on a crumbling board, not just a wrong tap. A dropped target is arguably slowness rather than
   * carelessness, but the overlay must never contradict the hearts the player just watched drain —
   * "no errors" appearing right after a heart is lost reads as a bug.
   */
  cleanBoardStreak = 0;
  private boardHadError = false;
  /**
   * Set the moment this game is over (either exit), so the asset warm-up in advanceBoard cannot
   * restart the Board scene into a round that has already left for the Menu or the Summary — the
   * player can still tap "← Menü" (the HUD's button, which the board's input lock doesn't cover)
   * during the few frames the warm-up waits.
   */
  private leaving = false;
  /**
   * Journey stars earned since this game started — the rung a good player climbs within a session.
   * Starts at 0 every game (the lifetime star count is untouched) and speeds the board clock up by
   * 5 points each off a clock that starts 25% generous (src/game/level.ts). Drawn as the
   * stack on the right edge.
   */
  sessionStars = 0;
  /** The full-stack celebration is a once-per-game event, not once per board above the ceiling. */
  stackCelebrated = false;
  /**
   * Whether this run's Mars UFO has been banked. Once per game: the planets past Mars must not
   * each pay out again, and `sessionStars` is recomputed on every hit, so without the latch the
   * grant would repeat on every smash from the 50th on.
   */
  ufoAwarded = false;
  /** Same latch for this run's Saturn Diamond (specs/pairs-mode.md §2.2). */
  diamondAwarded = false;
  /** Same latch for this run's Neptune Ice Cube (specs/word-maze-mode.md §2.2). */
  catAwarded = false;
  cubeAwarded = false;
  /**
   * 🌌 The top of the ladder, once per run (specs/wagon-mode.md §2.2). Its own flag like the three
   * below it, so a run pays each treat exactly once.
   */
  galaxyAwarded = false;

  /** Filling the stack banks a Word Labyrinth run — once per game (specs/labyrinth-mode.md §2.2). */
  satAwarded = false;

  /** The eclipse banks a Recipe Kitchen run — once per game (specs/recipe-mode.md §2.3). */
  pastaAwarded = false;

  /** The 🪱 Space Worm of star 13 — once per run, like every other treat (specs/space-worm-mode.md §2.2). */
  wormAwarded = false;

  /** The 🚀 Rocket of star 15 — once per run, Rocket Read languages only (specs/rocket-read-mode.md §2). */
  rocketAwarded = false;
  /**
   * Recent board-clear times (ms), newest first — the last SPREAD_WINDOW of them. Session-local and
   * never persisted; reset in create(), so returning to the menu and replaying starts fresh.
   *
   * Two statistics are read off it: the MEDIAN of the newest few (the player's pace) and a robust
   * SPREAD over the whole window (how much they vary). Together they set the Crumbling Board
   * threshold — see specs/adaptive-difficulty.md §1 and specs/crumbling-board.md §2.
   */
  recentBoardMs: number[] = [];

  private pack!: Pack;
  /** Every lesson has its own progression now. Reading pools build their boards through it
   *  (`drivesBoards`); arcade lessons build boards themselves and only report shown tiles. */
  progression: ReadingProgression | null = null;
  private drivesBoards = false;
  private boardStartedAt = 0;
  private totalPausedMs = 0;
  private pauseStartedAt = 0;
  /** The two independent reasons the board clock can be frozen (see create()). */
  private scenePaused = false;
  private appHidden = false;
  private lastClassId: string | null = null;
  private lastSetId: string | null = null;
  private lastContextId: string | null = null;
  /**
   * App Clip trial state, resolved once in create(). In the clip each lesson plays
   * boardsPerLesson() boards and then forwards to the paid app; `clipKey` is the lesson the
   * count belongs to (packKey — `<packId>__<lang>`), so every lesson in every language keeps its
   * own ten. Both are inert everywhere else: `clipMode` is false in the web, TWA and paid-app
   * builds, and nothing below branches on `clipKey` without it. See src/game/clip.ts.
   */
  private clipMode = false;
  private clipKey = '';

  constructor() {
    super({ key: 'Round' });
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    // The selected lesson (a Pack) drives the round. A reading-250 pool runs the spaced-repetition
    // learning journey (the engine picks each board); arcade packs run the classic random-set or
    // banner-context mode. Either way the lesson keeps its OWN progress — the engine tracks mastery
    // of the words shown (per-lesson store), so every lesson has an independent journey bar.
    this.pack = getActivePack();
    this.progression = new ReadingProgression(this.pack.lang, this.pack);
    this.drivesBoards = isJourneyPool(this.pack);
    this.progression.beginSession();
    this.clipMode = isClipMode();
    this.clipKey = packKey(this.pack);

    this.lives = STARTING_LIVES;
    this.score = 0;
    this.leaving = false;
    this.boardNumber = 1;
    this.boardStartedAt = 0;
    this.cleanBoardStreak = 0;
    this.boardHadError = false;
    this.sessionStars = 0;
    this.stackCelebrated = false;
    this.ufoAwarded = false;
    this.diamondAwarded = false;
    this.catAwarded = false;
    this.cubeAwarded = false;
    this.galaxyAwarded = false;
    this.satAwarded = false;
    this.pastaAwarded = false;
    this.wormAwarded = false;
    this.rocketAwarded = false;
    this.lastClassId = null;
    this.bestRoundMs = getBestRoundMs();
    this.recentBoardMs = [];

    trackRoundStart(this.clipKey);   // web demo only; a no-op in the clip, both apps and plain web

    // The backdrop is a scene of its own, launched here and stopped with the round. It has to sit
    // outside BoardScene because that scene is restarted between boards and renders nothing while
    // the new board loads — see BackdropScene.
    this.scene.launch('Backdrop');
    this.scene.launch('HUD');
    const { rule, set } = this.nextBoardConfig();
    this.scene.launch('Board', {
      rule, set, pack: this.pack, boardNumber: this.boardNumber,
      crumbleAfterMs: this.crumbleAfterMs(),
      paceMs: pace(this.recentBoardMs),
    });

    this.input.keyboard?.on('keydown-ESC', this.backToMenu, this);

    // Hold the screen awake for the whole round, not per board: reading a board is slow, deliberate
    // work — a player can look at four words for a minute without touching the glass, which is
    // exactly when the phone dims and locks. Round scope rather than BoardScene's covers the gaps
    // between boards (banner, next-board setup) without releasing and re-taking the lock a few
    // hundred times a session. Best-effort everywhere; see src/platform/wakeLock.ts.
    const wakeLock = screenWakeLock();
    wakeLock.acquire();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => wakeLock.release());

    // The board clock is wall-time (performance.now), so it must be frozen whenever gameplay
    // isn't actually running. Two independent causes, reconciled in updateClockPause():
    //  - scene pause (Help overlay) → scene PAUSE/RESUME events;
    //  - app backgrounded (tab hidden / home button) → game HIDDEN/VISIBLE events. Phaser pauses
    //    its loop then but does NOT pause scenes, so without this hook background time would
    //    count toward the round — inflating times and instantly tripping the crumbling-board
    //    threshold on return.
    this.scenePaused = false;
    this.appHidden = false;
    this.events.on(Phaser.Scenes.Events.PAUSE, () => { this.scenePaused = true; this.updateClockPause(); });
    this.events.on(Phaser.Scenes.Events.RESUME, () => { this.scenePaused = false; this.updateClockPause(); });
    const onHidden = (): void => { this.appHidden = true; this.updateClockPause(); };
    const onVisible = (): void => { this.appHidden = false; this.updateClockPause(); };
    this.game.events.on(Phaser.Core.Events.HIDDEN, onHidden);
    this.game.events.on(Phaser.Core.Events.VISIBLE, onVisible);
    // Game events are global — detach on scene shutdown or re-entering the Round would stack them.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off(Phaser.Core.Events.HIDDEN, onHidden);
      this.game.events.off(Phaser.Core.Events.VISIBLE, onVisible);
    });
  }

  backToMenu(): void {
    this.leaving = true;
    // Web demo only, and the reason this exit is instrumented at all: leaving via Back is how a
    // lot of games actually end, and without it those games would be missing from the reports
    // entirely — making every demo visitor look like they either died or hit the cap.
    trackRoundEnd(
      this.clipKey, this.boardNumber, 'quit', this.sessionStars, milestoneReached(this.sessionStars),
    );
    // A game that ends by backing out is still a game the child played — the scoreboard's furthest
    // round and best planet-stack run are recorded here too, or every careful player who never
    // plays to their last heart would have an empty record card (specs/scoreboard.md §4.1).
    this.progression?.recordGameEnd(this.boardNumber, this.sessionStars);
    // Backing out is still a round played, which is what the desktop QR overlay waits for before
    // it appears (src/game/qrPromo.ts). Recorded at both exits; a no-op off desktop web.
    recordRoundPlayed();

    // Carry this session's gains to the menu so its journey bar can celebrate newly mastered
    // words with the same falling-star animation as the end-of-round summary. Omitted when
    // nothing new was mastered, so a plain back-out stays instant.
    const summary = this.progression?.getSessionSummary() ?? null;
    this.scene.stop('Board');
    this.scene.stop('HUD');
    this.scene.stop('Backdrop');
    // Mastering the last word mid-round and then backing out is a real exit, not a skipped one:
    // the celebration is owed and this is the first chance to pay it. Without this the screen was
    // lost for good, because the planet stamp had already made "reached this session" false.
    if (this.progression?.celebrationPending()) {
      this.scene.start('Celebration', { score: this.score, summary });
      return;
    }
    // `fromRound` marks this as the end of a game, so the Menu shows the same web buy card the
    // Summary screen would have. Leaving via Back is how a lot of sessions actually end — without
    // it, a player who never plays a round out to its last heart would never see the card.
    this.scene.start('Menu', {
      fromRound: true,
      ...(summary && summary.newlyMastered.length > 0 ? { summary } : {}),
    });
  }

  /** Picks the set and rule for the next board, branching on the pack's mode. */
  /**
   * How long a board may run before it starts to crumble:
   *
   *     threshold = max( pace × exp(k × spread), floor )
   *
   * `pace` is the median of the last 3 board times and `spread` a robust deviation over the last 8,
   * both from THIS player (src/game/crumble.ts). So `k` asks everyone the same question in units
   * they each supply themselves — how many of your own typical deviations may a board run over your
   * own typical time — and the mechanic never needs to know who is holding the device. See
   * specs/adaptive-difficulty.md §1 for why a fixed multiple of the median could not do that.
   *
   * Lessons whose *question* lives in the banner get a more forgiving `k`, because the banner has to
   * be read before any tile means anything: picture boards (kindergarten decoding, §2.6 of
   * specs/picture-word-mode.md) and banner-context boards (a whole cloze sentence,
   * specs/gap-mode.md).
   */
  private crumbleAfterMs(): number | null {
    const [k, minMs] =
      this.pack.mode === 'picture-word' ? [CRUMBLE.pictureSpreadK, CRUMBLE.pictureMinMs]
      // Sentence boards share the banner clock for the same reason and then some: the strip has to
      // be read before a single tile means anything, and it is N decisions in a row, not one.
      : (this.pack.mode === 'banner-context' || this.pack.mode === 'sentence-order')
        ? [CRUMBLE.bannerSpreadK, CRUMBLE.bannerMinMs]
      : [CRUMBLE.spreadK, CRUMBLE.defaultMinMs];
    // The clock starts 25% more forgiving than the plain rule and each star earned this game takes
    // 5 points off it, down to a floor of 80% (src/game/level.ts). Applied to `k` — the number of
    // the player's OWN deviations they are allowed — so "harder" means the same statistical thing
    // for a first-grader and an adult, which a multiplier on the median never did. The mode's
    // absolute floor still wins underneath: a beginning reader's minimum reading time is not
    // something levelling may take away.
    // …and then a trim on the finished clock, which is the one thing `k` cannot express: a change to
    // `k` is worth exp(Δk × spread), so it takes 5% off a scattered player's clock and 0.7% off a
    // steady one's. The trim takes a straight percentage, graded by how long this player's boards
    // actually take — least from the slowest (src/game/level.ts).
    const paceMs = pace(this.recentBoardMs);
    const levelled = k * timeoutMultFor(this.sessionStars);
    return crumbleThresholdMs(
      paceMs, spreadRatio(this.recentBoardMs), levelled, minMs, clockTrimFor(paceMs),
    );
  }

  private nextBoardConfig(): { rule: BoardRule; set: PackSet } {
    if (this.progression && this.drivesBoards) {
      const { rule, set } = this.progression.buildBoard();
      return { rule, set };
    }
    const set = this.pickSet();
    if (this.pack.mode === 'banner-context') {
      const rule = this.buildBannerContextRule(set);
      return { rule, set };
    }
    if (this.pack.mode === 'sentence-order') {
      return { rule: this.buildSentenceRule(set), set };
    }
    const classes = this.pack.classes;
    const candidates = this.lastClassId !== null && classes.length > 1
      ? classes.filter((c) => c.id !== this.lastClassId)
      : classes;
    const cls = candidates[Math.floor(Math.random() * candidates.length)];
    this.lastClassId = cls.id;
    return { rule: new MatchClass(cls), set };
  }

  /**
   * Dev/staging only: `?set=<prefix>` narrows every board to the sets whose id starts with
   * `<prefix>`. Inert on production (devParam), and inert on any lesson with no matching set — a
   * prefix that matches nothing returns null and the full list is used.
   *
   * It exists for the store captures. The gaps lesson carries two kinds of puzzle in ONE pack —
   * word endings (`Kartof___`) and cloze sentences ("The dog ___ at the cat.") — and a random
   * board is as likely to be a bare stem as a sentence. The stem reads as a truncated string at
   * Play-thumbnail size, so the capture pins the set that shows what the lesson is for.
   *
   * The caller passes a concrete id, not a category: set ids are authored in each pack's own
   * language (`sentences-verbs`, `satz-verben`, `phrases-verbes`, …), so the renderer derives the
   * right one from the pack data — scripts/lib/gap-sets.mjs.
   */
  private forcedSets(): PackSet[] | null {
    const prefix = devParam('set');
    if (!prefix) return null;
    const matches = this.pack.sets.filter((s) => s.id.startsWith(prefix));
    return matches.length > 0 ? matches : null;
  }

  private pickSet(): PackSet {
    const sets = this.forcedSets() ?? this.pack.sets;
    const candidates = this.lastSetId !== null && sets.length > 1
      ? sets.filter((s) => s.id !== this.lastSetId)
      : sets;
    const set = candidates[Math.floor(Math.random() * candidates.length)];
    this.lastSetId = set.id;
    return set;
  }

  /** Picks one context word (e.g. a noun) from the set and builds a MatchTagForBannerContext rule. */
  private buildBannerContextRule(set: PackSet): BoardRule {
    const ctxClass = this.pack.bannerContextClass;
    const candidateClass = this.pack.candidateClass;
    const forKey = this.pack.forKey;
    if (!ctxClass || !candidateClass || !forKey) {
      throw new Error(`Pack ${this.pack.id} is mode 'banner-context' but missing bannerContextClass/candidateClass/forKey.`);
    }
    const contextWords = set.words.filter((w) => wordInClass(w, ctxClass));
    if (contextWords.length === 0) {
      throw new Error(`Pack ${this.pack.id}, set ${set.id}: no context words of class '${ctxClass}'.`);
    }
    const wordId = (w: PackWord) => (typeof w.id === 'string' ? w.id : w.text);
    const pool = this.lastContextId !== null && contextWords.length > 1
      ? contextWords.filter((w) => wordId(w) !== this.lastContextId)
      : contextWords;
    const ctx = pool[Math.floor(Math.random() * pool.length)];
    this.lastContextId = wordId(ctx);
    const bannerText = typeof ctx.banner === 'string' ? ctx.banner : ctx.text;
    return new MatchTagForBannerContext({
      candidateClassId: candidateClass,
      contextWord: ctx,
      bannerText,
      forKey,
    });
  }

  /**
   * Builds the sentence-order rule for a set (specs/sentence-order-mode.md §2). Unlike
   * banner-context there is nothing to pick: a set holds exactly ONE sentence, so the set IS the
   * board — which is what keeps every board's decoys under the author's control instead of drawing
   * them from whatever else happens to share the set.
   *
   * The banner stays the constant instruction ("Bau den Satz!") rather than the sentence: the
   * sentence is the answer, and printing it above the tiles would be printing the solution.
   */
  private buildSentenceRule(set: PackSet): BoardRule {
    const ctxClass = this.pack.bannerContextClass;
    const candidateClass = this.pack.candidateClass;
    if (!ctxClass || !candidateClass) {
      throw new Error(`Pack ${this.pack.id} is mode 'sentence-order' but missing bannerContextClass/candidateClass.`);
    }
    const sentences = set.words.filter((w) => wordInClass(w, ctxClass));
    if (sentences.length !== 1) {
      throw new Error(`Pack ${this.pack.id}, set ${set.id}: expected exactly one '${ctxClass}' word, found ${sentences.length}.`);
    }
    const ctx = sentences[0];
    const csv = (v: unknown): string[] =>
      (typeof v === 'string' ? v : '').split(',').map((s) => s.trim()).filter(Boolean);
    const order = csv(ctx.folge);
    const roles = csv(ctx.rollen);
    if (order.length === 0 || order.length !== roles.length) {
      throw new Error(`Pack ${this.pack.id}, set ${set.id}: 'folge' and 'rollen' must be non-empty and the same length.`);
    }
    return new MatchNextInSentence({
      candidateClassId: candidateClass,
      prompt: this.pack.classes.find((c) => c.id === candidateClass)?.prompt ?? '',
      order,
      roles,
      vorgabe: typeof ctx.vorgabe === 'string' ? ctx.vorgabe : '',
      sentence: ctx.text,
    });
  }

  startBoardTimer(): void {
    this.boardStartedAt = performance.now();
    this.totalPausedMs = 0;
    this.pauseStartedAt = 0;
    // A pause reason can already be active when a new board's clock starts (its timer freezes
    // with the scene, so this runs right after a resume) — re-freeze immediately in that case.
    this.updateClockPause();
  }

  /** Freeze the board clock while any pause reason is active; unfreeze once all clear. */
  private updateClockPause(): void {
    const shouldPause = this.scenePaused || this.appHidden;
    if (shouldPause && this.pauseStartedAt === 0 && this.boardStartedAt > 0) {
      this.pauseStartedAt = performance.now();
    } else if (!shouldPause && this.pauseStartedAt > 0) {
      this.totalPausedMs += performance.now() - this.pauseStartedAt;
      this.pauseStartedAt = 0;
    }
  }

  elapsedMs(): number {
    if (this.boardStartedAt === 0) return 0;
    const now = performance.now();
    const inFlightPause = this.pauseStartedAt > 0 ? (now - this.pauseStartedAt) : 0;
    return Math.max(0, now - this.boardStartedAt - this.totalPausedMs - inFlightPause);
  }

  registerHit(correct: boolean, points: number): void {
    if (correct) {
      this.score += points;
      this.events.emit('score-changed', this.score);
      // Star progress advances per CLICK, not per board — a meter that only moved at the board
      // break was static for the ~30s that actually matter. Driven off the score itself, so the
      // stack and the score readout can never disagree.
      const previousStars = this.sessionStars;
      const { stars, fraction } = starProgress(this.score);
      this.sessionStars = stars;
      this.events.emit('stars-changed', stars, fraction);
      // Web demo only. One event per level *crossed* rather than per hit, and only for the levels
      // the ladder actually names, so the unnamed stretch above the Sun stays quiet (milestones.ts).
      // A loop, not a single check: nothing guarantees a hit is worth less than a whole star.
      for (let level = previousStars + 1; level <= stars; level++) {
        const planet = milestoneAt(level);
        if (planet !== null) trackPlanetReached(this.clipKey, planet, level);
      }
      // Reaching Venus banks a Cat & Mouse run — the LOWEST rung of the ladder at 25 smashes
      // (specs/cat-mouse-mode.md §2). It is first here because it is first on the ladder: the
      // mode it buys is the one for a child who cannot yet read a word, so it must not sit behind
      // the reading the other six are priced in. Its own flag, like all of them.
      if (!this.catAwarded && stars >= CAT_AT_STACK_STARS) {
        this.catAwarded = true;
        this.progression?.grantVenusCat();
        this.events.emit('cat-earned');
      }
      // Reaching Mars banks a Survival run — the one reward a first-session player can actually
      // reach, since the star-driven UFO needs words MASTERED (progression/config.ts).
      if (!this.ufoAwarded && stars >= UFO_AT_STACK_STARS) {
        this.ufoAwarded = true;
        this.progression?.grantStackUfo();
        // Banked here and now (a run that ends on this very board keeps it); the HUD shows it on
        // the planet that earned it, a second behind the star chime.
        this.events.emit('ufo-earned');
      }
      // Reaching Saturn banks a Pairs run — the same shape as the Mars UFO above, one rung and a
      // half further up (specs/pairs-mode.md §2). Separate flag, so a run pays each treat once.
      if (!this.diamondAwarded && stars >= DIAMOND_AT_STACK_STARS) {
        this.diamondAwarded = true;
        this.progression?.grantSaturnDiamond();
        this.events.emit('diamond-earned');
      }
      // Reaching Neptune banks an Ice Maze run — the third and last rung of the treat ladder,
      // 50/75/100 smashes (specs/word-maze-mode.md §2). Its own flag, like the two above.
      if (!this.cubeAwarded && stars >= ICE_AT_STACK_STARS) {
        this.cubeAwarded = true;
        this.progression?.grantNeptuneCube();
        this.events.emit('cube-earned');
      }
      // The galaxy — the top of the whole ladder, where the Sun's slot stops being a star — banks a
      // Word Wagon run (specs/wagon-mode.md §2). Far above the other three (213 smashes against
      // 100) because it is not a rung of the treat ladder at all: it is the last thing the ladder
      // draws, and until now the one named milestone with nothing behind it.
      if (!this.galaxyAwarded && stars >= GALAXY_AT_STACK_STARS) {
        this.galaxyAwarded = true;
        this.progression?.grantGalaxy();
        this.events.emit('galaxy-earned');
      }
      // Filling the whole stack banks a Word Labyrinth run — star 9, the Sun, 113 smashes
      // (specs/labyrinth-mode.md §2). Its own flag, like the three above, so a run pays each once.
      // The HUD holds the flourish until BoardScene's full-stack send-off has finished, because
      // this is the one treat that shares a star with it.
      if (!this.satAwarded && stars >= SAT_AT_STACK_STARS) {
        this.satAwarded = true;
        this.progression?.grantSunSatellite();
        this.events.emit('sat-earned');
      }
      // The eclipse banks a Recipe Kitchen run — star 14, 175 smashes (specs/recipe-mode.md §2).
      // Its own flag, like the four above, so a run pays each treat exactly once. The HUD holds
      // this flourish until any board-break celebration has finished; see HUDScene.
      if (!this.pastaAwarded && stars >= PASTA_AT_STACK_STARS) {
        this.pastaAwarded = true;
        this.progression?.grantEclipsePlate();
        this.events.emit('pasta-earned');
      }
      // The red Sun banks a Space Worm run — star 13, 163 smashes (specs/space-worm-mode.md §2).
      // The first prize above the full stack, and what fills the ladder's longest unpaid climb.
      // Its own flag, like the six above, so a run pays each treat exactly once.
      if (!this.wormAwarded && stars >= WORM_AT_STACK_STARS) {
        this.wormAwarded = true;
        this.progression?.grantRedSunWorm();
        this.events.emit('worm-earned');
      }
      // The violet Sun banks a Rocket Read run — star 15, 188 smashes (specs/rocket-read-mode.md
      // §2). Only where the mode has content (§2.3): elsewhere star 15 stays unpaid, as before.
      if (!this.rocketAwarded && stars >= ROCKET_AT_STACK_STARS && isRocketReadLang(this.pack.lang)) {
        this.rocketAwarded = true;
        this.progression?.grantVioletSunRocket();
        this.events.emit('rocket-earned');
      }
    } else {
      this.boardHadError = true;
      this.lives = Math.max(0, this.lives - 1);
      this.events.emit('lives-changed', this.lives);
      if (this.lives === 0) this.endRound();
    }
  }

  /**
   * Reports the poppable tiles for the board just laid out, so an arcade lesson's progression can
   * track which words were shown (introduce on first sight, count exposure). No-op when the engine
   * drives the boards itself (reading mode), since buildBoard already did this bookkeeping.
   */
  onBoardShown(shownWords: PackWord[]): void {
    if (this.progression && !this.drivesBoards) this.progression.trackExternalBoard(shownWords);
  }

  /** Record a per-word pop result (target popped, or decoy wrongly popped). */
  onWordResolved(word: PackWord, correct: boolean): void {
    this.progression?.recordHit(word, correct);
    if (correct) registerSmash();   // the day streak (specs/day-streak.md); a no-op in the clip/demo
  }

  /**
   * A target word fell off the bottom during a board collapse (Crumbling Board). Costs a life —
   * like a wrong tap, but with its own "fell off" cue (played by BoardScene), not the wrong-tap
   * buzzer — and records the word as a miss for mastery. Ends the round if it empties lives.
   */
  onTargetDropped(word: PackWord): void {
    this.onWordResolved(word, false);
    this.boardHadError = true;
    this.lives = Math.max(0, this.lives - 1);
    this.events.emit('lives-changed', this.lives);
    if (this.lives === 0) this.endRound();
  }

  /** Current journey stats for the active lesson (null only before create()). */
  progressionStats(): PoolStats | null {
    return this.progression?.stats() ?? null;
  }

  completeBoard(): BoardCompletion {
    const elapsedMs = this.elapsedMs();
    let isRecord = false;
    if (this.bestRoundMs === null || elapsedMs < this.bestRoundMs) {
      this.bestRoundMs = elapsedMs;
      setBestRoundMs(elapsedMs);
      isRecord = true;
      this.events.emit('best-changed', this.bestRoundMs);
    }
    // The lesson's OWN record card: this board counted, and its time if it is a personal best for
    // this lesson (specs/scoreboard.md §4.1). Separate from the global best above on purpose — see
    // §1.1: one number across ten differently shaped lessons cannot be beaten in all of them.
    this.progression?.recordBoardCleared(elapsedMs);

    // Session-local record that drives the Crumbling Board threshold (never persisted).
    this.recentBoardMs = pushBoardTime(this.recentBoardMs, elapsedMs);
    this.boardStartedAt = 0;

    // Accuracy streak: a clean board extends it, any lost heart has already broken it. Counted
    // here (on completion) rather than per-hit so an abandoned board never banks a clean run.
    this.cleanBoardStreak = nextCleanStreak(this.cleanBoardStreak, this.boardHadError);
    this.boardHadError = false;

    // Reading mode: finalize the board (decoys correctly avoided count as correct) and
    // push fresh journey stats to the HUD.
    if (this.progression) {
      this.progression.finishBoard();
      this.events.emit('progress-changed', this.progression.stats());
    }

    // App Clip: this board counts against the lesson's allowance. Counted on COMPLETION, so a board
    // abandoned via Back — or one the player lost their last life on — never silently eats part
    // of the trial. advanceBoard() below is what acts on the result.
    if (this.clipMode) recordClipBoard(this.clipKey);

    trackBoardCleared(this.clipKey, this.boardNumber);   // web demo only

    return { elapsedMs, isRecord };
  }

  /**
   * Whether another board follows the one just cleared. Only the App Clip's per-lesson cap can
   * answer "no" — everywhere else a cleared board is always followed by the next one, and the
   * round ends on lives, not on a count. BoardScene asks so its "Round N+1" banner doesn't
   * promise a board that will never come.
   */
  hasNextBoard(): boolean {
    return !(this.clipMode && isClipLessonSpent(this.clipKey));
  }

  /**
   * The App Clip's trial position for the HUD, e.g. `2/3`; null outside the clip, where the plain
   * board number is the honest readout. Shown from the first board on purpose — a cap the parent
   * only discovers when it stops them reads as a trick.
   */
  clipTrialLabel(): string | null {
    if (!this.clipMode) return null;
    const cap = boardsPerLesson();
    // The demo's Saturn run has no board cap (clip.ts demoRunsToSaturn) — it ends on a score, and
    // there is nothing here worth printing: "3/∞" is noise and "3/75 smashes" is a different HUD.
    if (!Number.isFinite(cap)) return null;
    const played = Math.min(clipBoardsPlayed(this.clipKey) + 1, cap);
    return `${played}/${cap}`;
  }

  advanceBoard(): void {
    // App Clip: the lesson has played its allowance. End the round into the Summary, which is where
    // the end-of-trial card lives (src/game/ui/clipOutro.ts) — the player still gets their score
    // and their journey stars first, then the ask.
    if (!this.hasNextBoard()) {
      this.endRound();
      return;
    }
    this.boardNumber++;
    this.events.emit('round-changed', this.boardNumber);
    const board = this.scene.get('Board');
    const { rule, set } = this.nextBoardConfig();
    const init: BoardInit = {
      rule, set, pack: this.pack, boardNumber: this.boardNumber,
      crumbleAfterMs: this.crumbleAfterMs(),
      paceMs: pace(this.recentBoardMs),
    };
    this.warmBoardAssets(rule, set, () => board.scene.restart(init));
  }

  /**
   * Load the next board's word icons BEFORE restarting the Board scene, then restart.
   *
   * Loading them from BoardScene.preload instead — which is where they used to be loaded, and
   * still are as a fallback — costs the board its first frames: Phaser holds a scene with queued
   * files in LOADING and renders none of it until they arrive, so a picture board fetching four
   * fresh .webp files went missing for about five frames on every restart. Waiting here spends
   * those frames on the board the child has just finished, which is already on screen and holding
   * still, and the restart that follows finds every texture cached and runs create() at once.
   *
   * The wait is capped, because a slow or failed fetch must never strand the player on a finished
   * board: past the cap we restart anyway and BoardScene's preload picks the load up. That path is
   * no longer a blank screen either — the backdrop lives in BackdropScene now.
   */
  private warmBoardAssets(rule: BoardRule, set: PackSet, restart: () => void): void {
    const missing = boardIconAssets(rule, set).filter(({ key }) => !this.textures.exists(key));
    if (missing.length === 0) {
      restart();
      return;
    }
    let started = false;
    const go = (): void => {
      // Both the loader and the timeout call this, and the game can end underneath either of them
      // (the HUD's menu button stays live while we wait) — a restart then would raise the Board
      // scene back over the Menu.
      if (started || this.leaving) return;
      started = true;
      restart();
    };
    for (const { key, url } of missing) this.load.image(key, url);
    this.load.once(Phaser.Loader.Events.COMPLETE, go);
    this.time.delayedCall(WARM_TIMEOUT_MS, go);
    this.load.start();
  }

  endRound(): void {
    this.leaving = true;
    // Web demo only. `lives_out` is the game beating them; `completed` means there was no next
    // board, which in a demo session is precisely the trial cap — the two ways a demo round ends,
    // and the reason a demo player did or did not reach the sale card.
    trackRoundEnd(
      this.clipKey, this.boardNumber, this.lives === 0 ? 'lives_out' : 'completed',
      this.sessionStars, milestoneReached(this.sessionStars),
    );
    // The other exit the scoreboard records (see backToMenu). `boardNumber` is the round the HUD
    // was showing when the last heart went — the round they REACHED, not the last one cleared.
    this.progression?.recordGameEnd(this.boardNumber, this.sessionStars);
    recordRoundPlayed();

    this.scene.stop('Board');
    this.scene.stop('HUD');
    this.scene.stop('Backdrop');
    const summary = this.progression?.getSessionSummary() ?? null;
    // Completing the whole lesson (all its words mastered) earns a dedicated celebration screen
    // INSTEAD of the usual "Spiel beendet" summary. Gated on the persisted debt rather than on
    // "reached this session", so an owed celebration survives a back-out or an app kill.
    // `?planet` forces it on every game-over so the screen is testable without mastering everything.
    if ((this.progression?.celebrationPending() ?? false) || devFlag('planet')) {
      this.scene.start('Celebration', { score: this.score, summary });
      return;
    }
    this.scene.start('Summary', { score: this.score, summary, stackStars: this.sessionStars });
  }
}
